import { booleanAttribute, Component, computed, input, ChangeDetectionStrategy } from '@angular/core';
import { DARK_THEME_CONFIG } from './config/dark-theme.config';
import { LIGHT_THEME_CONFIG } from './config/light-theme.config';
import { SIZE_CONFIG } from './config/size.config';
import { ButtonMode, ButtonSize, ButtonTheme } from './button.models';

@Component({
  selector: 'mib-button',
  templateUrl: './button.component.html',
  host: { class: 'block w-fit' },
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MibButtonComponent {
  readonly type = input<'button' | 'submit' | 'reset'>('button');
  readonly disabled = input(false, { transform: booleanAttribute });
  readonly active = input(false, { transform: booleanAttribute });
  readonly iconOnly = input(false, { transform: booleanAttribute });
  readonly darkMode = input(false, { transform: booleanAttribute });
  readonly alignLeft = input(false, { transform: booleanAttribute });
  readonly withoutPadding = input(false, { transform: booleanAttribute });
  readonly heightClass = input<string>();

  readonly size = input<ButtonSize>('large');
  readonly theme = input<ButtonTheme>('primary');
  readonly mode = input<ButtonMode>('filled');
  readonly shape = input<string>('rounded-sm');

  readonly effectiveShape = computed(() => this.shape() || 'rounded-sm');
  readonly classes = computed(() => {
    const classes: string[] = [
      // Base styles
      'flex',
      'items-center',
      'box-border',
      'select-none',
      'transition-colors',
      'border',
    ];

    // Alignment
    if (!this.alignLeft()) {
      classes.push('justify-center');
    }

    // Focus styles
    classes.push('focus-visible:outline-2', 'outline-mib-primary', 'outline-offset-2');

    // Shape
    classes.push(this.effectiveShape());

    // Cursor
    classes.push(this.disabled() ? 'cursor-default' : 'cursor-pointer');

    // Size classes
    classes.push(...this.getSizeClasses());

    // Theme classes
    classes.push(...this.getThemeClasses());

    return classes.join(' ');
  });

  /**
   * Returns the size-related CSS classes based on button size and layout configuration
   */
  private getSizeClasses(): string[] {
    const sizeConfig = SIZE_CONFIG[this.size()];
    const classes: string[] = [];

    // Width
    if (this.iconOnly()) {
      classes.push(sizeConfig.width.iconOnly);
    } else {
      classes.push(sizeConfig.width.regular);
      // Padding
      if (!this.withoutPadding()) {
        classes.push(this.alignLeft() ? sizeConfig.padding.alignLeft : sizeConfig.padding.regular);
      }
    }

    // Height, text size, and gap
    classes.push(sizeConfig.text, sizeConfig.gap);
    const externalHeightClass = this.heightClass();
    if (!externalHeightClass) {
      classes.push(sizeConfig.height);
    } else {
      classes.push(externalHeightClass);
    }

    return classes;
  }

  /**
   * Returns the theme-related CSS classes based on theme, mode, dark mode, and state
   */
  private getThemeClasses(): string[] {
    // Select the appropriate theme configuration
    const themeConfig = this.darkMode() ? DARK_THEME_CONFIG : LIGHT_THEME_CONFIG;

    // Get the specific configuration for the current theme and mode
    const modeConfig = themeConfig[this.theme()][this.mode()];

    // Determine which state configuration to use
    let stateConfig;
    if (this.disabled()) {
      stateConfig = modeConfig.disabled;
    } else if (this.active()) {
      stateConfig = modeConfig.active;
    } else {
      stateConfig = modeConfig.enabled;
    }

    // Return the pre-configured class strings
    return [stateConfig.bg, stateConfig.text, stateConfig.border];
  }
}
